import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  CircularProgress,
  Stack,
  TextField,
  InputAdornment
} from '@mui/material';
import {
  Visibility,
  Delete,
  Search,
  Route as RouteIcon,
  Timeline,
  Terrain,
  Flag,
  Upload,
  ArrowBack
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

interface RouteMetadata {
  raceId: string;
  name: string;
  totalDistance: number;
  lastUpdated: string;
  // Optional detailed fields (for when we need full route data)
  elevationProfile?: {
    totalGain: number;
    totalLoss: number;
    maxElevation: number;
  };
  waypoints: number;
  segments: number;
  createdAt?: string;
  updatedAt?: string;
}

const RouteList: React.FC = () => {
  const [routes, setRoutes] = useState<RouteMetadata[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    raceId: string;
    name: string;
  }>({ open: false, raceId: '', name: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [deleting, setDeleting] = useState<string | null>(null);
  const hasFetched = useRef(false);

  const navigate = useNavigate();

  useEffect(() => {
    if (!hasFetched.current) {
      hasFetched.current = true;
      fetchRoutes();
    }
  }, []);

  const fetchRoutes = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${process.env.REACT_APP_API_URL}/routes/list`);

      if (!response.ok) {
        throw new Error('Failed to fetch routes');
      }

      const result = await response.json();
      setRoutes(result.data?.routes || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load routes');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRoute = async (raceId: string) => {
    try {
      setDeleting(raceId);

      const response = await fetch(`${process.env.REACT_APP_API_URL}/routes/${raceId}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        throw new Error('Failed to delete route');
      }

      // Remove from local state
      setRoutes(prev => prev.filter(route => route.raceId !== raceId));
      setDeleteDialog({ open: false, raceId: '', name: '' });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete route');
    } finally {
      setDeleting(null);
    }
  };

  const handleViewRoute = (raceId: string) => {
    navigate(`/routes/view/${raceId}`);
  };

  const formatDistance = (km: number): string => {
    // For very small distances, show more precision or convert to meters
    if (km < 0.1) {
      const meters = km * 1000;
      return `${meters.toFixed(0)}m`;
    }
    return `${km.toFixed(1)} km`;
  };

  const formatElevation = (meters: number): string => {
    return `${meters.toLocaleString()}m`;
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };



  const filteredRoutes = routes.filter(route =>
    route.raceId.toLowerCase().includes(searchTerm.toLowerCase()) ||
    route.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/dashboard')}
            sx={{ mb: 1 }}
          >
            Back to Dashboard
          </Button>
          <Typography variant="h4">
            Route Management
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Upload />}
          onClick={() => navigate('/routes/upload')}
        >
          Upload Route
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Search Bar */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search routes by Race ID or name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            )
          }}
        />
      </Paper>

      {/* Routes Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Race ID</TableCell>
              <TableCell>Route Name</TableCell>
              <TableCell align="right">Distance</TableCell>
              <TableCell align="right">Elevation</TableCell>
              <TableCell align="center">Segments</TableCell>
              <TableCell align="center">Waypoints</TableCell>
              <TableCell align="center">Created</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRoutes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <Stack spacing={2} alignItems="center">
                    <RouteIcon sx={{ fontSize: 48, color: 'grey.400' }} />
                    <Typography variant="h6" color="text.secondary">
                      {routes.length === 0 ? 'No routes uploaded yet' : 'No routes match your search'}
                    </Typography>
                    {routes.length === 0 && (
                      <Button
                        variant="contained"
                        startIcon={<Upload />}
                        onClick={() => navigate('/routes/upload')}
                      >
                        Upload First Route
                      </Button>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            ) : (
              filteredRoutes.map((route) => (
                <TableRow key={route.raceId} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {route.raceId}
                    </Typography>
                  </TableCell>
                  <TableCell>{route.name}</TableCell>
                  <TableCell align="right">
                    <Chip
                      icon={<RouteIcon />}
                      label={formatDistance(route.totalDistance)}
                      size="small"
                      color="primary"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Stack spacing={0.5} alignItems="flex-end">
                      <Chip
                        icon={<Terrain />}
                        label={route.elevationProfile ? `+${formatElevation(route.elevationProfile.totalGain)}` : 'N/A'}
                        size="small"
                        color="success"
                      />
                      {route.elevationProfile && (
                        <Typography variant="caption" color="text.secondary">
                          Max: {formatElevation(route.elevationProfile.maxElevation)}
                        </Typography>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      icon={<Timeline />}
                      label={route.segments}
                      size="small"
                      color={route.segments > 5 ? 'warning' : 'default'}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      icon={<Flag />}
                      label={route.waypoints || 0}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="caption">
                      {formatDate(route.lastUpdated)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={1} justifyContent="center">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleViewRoute(route.raceId)}
                        title="View route details"
                      >
                        <Visibility />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setDeleteDialog({
                          open: true,
                          raceId: route.raceId,
                          name: route.name
                        })}
                        disabled={deleting === route.raceId}
                        title="Delete route"
                      >
                        {deleting === route.raceId ? (
                          <CircularProgress size={16} />
                        ) : (
                          <Delete />
                        )}
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Statistics Summary */}
      {routes.length > 0 && (
        <Paper sx={{ p: 2, mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Route Statistics
          </Typography>
          <Stack direction="row" spacing={3}>
            <Chip
              label={`${routes.length} Total Routes`}
              color="primary"
            />
            <Chip
              label={`${routes.reduce((sum, r) => sum + r.totalDistance, 0).toFixed(0)} km Total Distance`}
              color="info"
            />
            <Chip
              label={`${routes.reduce((sum, r) => sum + (r.elevationProfile?.totalGain || 0), 0).toLocaleString()}m Total Elevation`}
              color="success"
            />
            <Chip
              label={`${routes.reduce((sum, r) => sum + r.segments, 0)} Total Segments`}
              color="warning"
            />
          </Stack>
        </Paper>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, raceId: '', name: '' })}
      >
        <DialogTitle>Delete Route</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the route "{deleteDialog.name}" (ID: {deleteDialog.raceId})?
            This action cannot be undone and will remove all route data including coordinates,
            elevation profiles, waypoints, and segments.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, raceId: '', name: '' })}>
            Cancel
          </Button>
          <Button
            onClick={() => handleDeleteRoute(deleteDialog.raceId)}
            color="error"
            variant="contained"
            disabled={deleting === deleteDialog.raceId}
          >
            {deleting === deleteDialog.raceId ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                Deleting...
              </>
            ) : (
              'Delete Route'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RouteList;